"use client";

import Link from "next/link";
import { motion } from "framer-motion";
import type { ReactNode } from "react";
import type { Game } from "@/lib/data";
import { cn } from "@/lib/cn";
import { EASE } from "@/lib/motion";
import { Icon, type IconName } from "@/components/ui/Icon";
import { Panel, PanelHeader } from "@/components/ui/primitives";
import { GameTile } from "./GameTile";
import { RailArrows, RailTrack, useRail } from "./Rail";

/* `panel` is the homepage's section: a titled panel with the rail inside.
   `plain` is the kit's uncontained section, as the casino lobby draws it:
   the title on the canvas with "View All" and chevron pills beside it, the
   rail right under. `compact` lays the row out on the lobby's smaller
   tile. */
export type GameSectionVariant = "panel" | "plain";

export function GameSection({
  icon,
  title,
  href = "/c/all",
  games,
  rows = 1,
  featured,
  ranked = false,
  variant = "panel",
  compact = false,
}: {
  icon: IconName;
  title: string;
  href?: string;
  games: Game[];
  rows?: 1 | 2;
  featured?: boolean;
  /* The first ten in one row, each beside its rank in large outlined
     figures (Rigel's Trending). */
  ranked?: boolean;
  variant?: GameSectionVariant;
  compact?: boolean;
}) {
  const rail = useRail();

  if (games.length === 0) return null;

  /* Two rows only pay off once there are enough tiles to fill both across the
     kit's seven visible columns. Below that the grid leaves half the panel
     empty, so a single row that runs the full width reads better. */
  const laidOutRows = games.length >= 14 ? rows : 1;

  const track = ranked ? (
    <RailTrack
      railRef={rail.ref}
      onScroll={rail.measure}
      padded={variant === "panel"}
      className={variant === "panel" ? "p-[16px]" : undefined}
    >
      <ol className="flex shrink-0 gap-x-[20px]">
        {games.slice(0, 10).map((g, i) => (
          <motion.li
            key={`${g.id}-${i}`}
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, ease: EASE, delay: Math.min(i * 0.04, 0.36) }}
            className="flex shrink-0 snap-start items-end"
          >
            <span
              aria-hidden
              className="relative -mr-[14px] select-none font-heading text-[104px] font-black leading-[0.78] text-transparent [-webkit-text-stroke:2px_var(--color-brand-fg-accent)] lg:text-[128px]"
            >
              {i + 1}
            </span>
            <span className="sr-only">Number {i + 1}: </span>
            <div className="relative w-[150px] lg:w-[158px]">
              <GameTile game={g} />
            </div>
          </motion.li>
        ))}
      </ol>
    </RailTrack>
  ) : (
    <RailTrack
      railRef={rail.ref}
      onScroll={rail.measure}
      padded={variant === "panel"}
      className={variant === "panel" ? "p-[16px]" : undefined}
    >
      {featured && (
        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, ease: EASE }}
          className="w-[327px] shrink-0 snap-start"
        >
          <GameTile game={games[0]} size="xl" />
        </motion.div>
      )}
      <div
        className="grid shrink-0 grid-flow-col gap-x-[12px]"
        style={{
          gridTemplateRows: `repeat(${laidOutRows}, minmax(0, 1fr))`,
          rowGap: featured ? 12 : 16,
        }}
      >
        {(featured ? games.slice(1) : games).map((g, i) => (
          <motion.div
            key={`${g.id}-${i}`}
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, ease: EASE, delay: Math.min(i * 0.03, 0.3) }}
            className={cn("snap-start", compact && !featured ? "w-[126px]" : "w-[158px]")}
          >
            <GameTile game={g} />
          </motion.div>
        ))}
      </div>
    </RailTrack>
  );

  if (variant === "plain") {
    return (
      <section className="flex flex-col gap-[16px]">
        <PlainSectionHeader icon={icon} title={title}>
          <Link
            href={href}
            className="inline-flex h-[32px] items-center rounded-rounded bg-elevation-2 px-[16px] text-[12px] font-bold leading-[1.6] text-fg-subtle transition-colors hover:bg-elevation-3 hover:text-fg"
          >
            View All
          </Link>
          <RailArrows state={rail.state} scrollBy={rail.scrollBy} size="sm" className="hidden sm:flex" />
        </PlainSectionHeader>
        {track}
      </section>
    );
  }

  return (
    <Panel className="overflow-hidden">
      <PanelHeader
        icon={icon}
        title={title}
        action={
          <>
            <Link
              href={href}
              className="hidden h-[38px] items-center rounded-lg bg-elevation-2 px-[12px] text-[12px] font-bold leading-[1.6] text-fg transition-colors hover:bg-elevation-3 sm:inline-flex"
            >
              View All
            </Link>
            <RailArrows state={rail.state} scrollBy={rail.scrollBy} />
          </>
        }
      />

      {track}
    </Panel>
  );
}

/* The kit's uncontained section header: icon and a large title at the left,
   the section's controls at the right, nothing behind them. */
export function PlainSectionHeader({
  icon,
  title,
  children,
}: {
  icon?: IconName;
  title: string;
  children?: ReactNode;
}) {
  return (
    <header className="flex h-[32px] items-center gap-[8px]">
      <div className="flex min-w-0 flex-1 items-center gap-[8px]">
        {icon && <Icon name={icon} size={20} className="shrink-0 text-brand-bold" />}
        <h2 data-section-title="" className="truncate text-[17.5px] font-extrabold leading-[1.2] lg:text-[22px]">{title}</h2>
      </div>
      {children && <div className="flex shrink-0 items-center gap-[4px]">{children}</div>}
    </header>
  );
}

export function GameGrid({ games }: { games: Game[] }) {
  return (
    <div className="grid gap-[12px] [grid-template-columns:repeat(auto-fill,minmax(120px,1fr))] sm:[grid-template-columns:repeat(auto-fill,minmax(158px,1fr))]">
      {games.map((g, i) => (
        <motion.div
          key={`${g.id}-${i}`}
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4, ease: EASE, delay: Math.min(i * 0.025, 0.35) }}
        >
          <GameTile game={g} />
        </motion.div>
      ))}
    </div>
  );
}

